import type { DispatchMetric } from '../../api/types';
import {
  type MetricType,
  KPI_PRESETS,
  categoryLabel,
  isFeedCategory,
  isSnapshotMetric,
  isSnapshotSubcategory,
  metricTypeOf,
  normalizeKpiHeader,
  sharedValueKind,
  subcategoriesFor,
  subcategoryLabel,
  valueKindOf,
} from './metricMeta';

/**
 * What the create/edit dialog holds, and what it puts on the wire.
 *
 * Kept out of the component because the interesting part is not the form — it
 * is the two fields the form DOES NOT show. dispatch stores a per-metric time
 * shape in `groupBy`/`arrangeBy`, and this screen deliberately has no control
 * for it: the window belongs to the page here (see frames.ts), and a control
 * that edited a metric's own window would be editing what the Flutter
 * dashboard draws, from a screen where the effect is invisible.
 *
 * So the pair is DERIVED, and the rule is the one the whole feature runs on:
 * **on create, offer a sensible default; on edit, keep what is stored unless
 * the edit made it impossible.** A metric created on the Flutter page as a
 * monthly chart stays monthly over there after somebody renames it here.
 *
 * Three impossibilities, which are the cases worth reading twice:
 *
 * - **`arrangeBy` on a KPI is not an arrangement, it is the group HEADER**,
 *   stored verbatim (metricMeta.ts says so, and so does the dashboard's
 *   `metrics_kpi_header.dart`). So switching a chart to a KPI must not carry
 *   `daily_over_month` over as a heading, and switching a KPI to a chart must
 *   not leave "KPI Row" where an arrangement belongs — `coerceArrangeBy`
 *   drops anything that is not an option for the grouping.
 * - **A snapshot carries no window at all** (`groupBy: 'all'`), so a metric
 *   that becomes one loses its grouping and a metric that stops being one has
 *   to be given a real grouping back.
 * - **A dispatch metric groups by day or week only**, mirroring the dashboard's
 *   `_availableGroupBy`; anything else is clamped.
 */

export interface MetricForm {
  category: string;
  subCategory: string[];
  type: MetricType;
  metricsName: string;
  /** The KPI group header. Empty means none; only a KPI sends it. */
  header: string;
}

/** dispatch's `MetricsArrangeBy`, and the options it offers per grouping. */
const ARRANGE_OPTIONS: Record<string, string[]> = {
  day: ['daily_over_month', 'day', 'week', 'month_weeks'],
  week: ['month_weeks', 'month'],
  month: ['month', 'quarter', 'year'],
  quarter: ['quarter', 'year', 'yoy'],
  year: ['year', 'yoy'],
};

const BUCKETS = ['day', 'week', 'month', 'quarter', 'year'];

/** The groupings a dispatch metric may use — the job board answers day or week. */
const FEED_BUCKETS = ['day', 'week'];

export const DEFAULT_BUCKET = 'day';
export const SNAPSHOT_BUCKET = 'all';

/** `MetricsArrangeBy.coerce`: keep it if the grouping offers it, else its default. */
const coerceArrangeBy = (groupBy: string, value: string): string => {
  const options = ARRANGE_OPTIONS[groupBy] ?? ARRANGE_OPTIONS[DEFAULT_BUCKET];
  const normalized = String(value || '').trim().toLowerCase();
  return options.includes(normalized) ? normalized : options[0];
};

const normalizeBucket = (value: string): string => {
  const lower = String(value || '').trim().toLowerCase();
  return BUCKETS.includes(lower) ? lower : '';
};

export const blankForm = (): MetricForm => ({
  category: 'jobs',
  subCategory: [],
  type: 'barChart',
  metricsName: '',
  header: '',
});

/**
 * A saved row as the dialog holds it.
 *
 * The header is read only off a KPI, because on a chart that same field is an
 * arrangement — offering `daily_over_month` as a heading is how it would end up
 * printed over a row of cards.
 */
export const formOf = (metric: DispatchMetric): MetricForm => {
  const type = metricTypeOf(metric.type);
  return {
    category: String(metric.category || '').toLowerCase(),
    subCategory: metric.subCategory.filter(Boolean),
    type,
    metricsName: metric.metricsName || '',
    header: type === 'kpi' ? metric.arrangeBy || '' : '',
  };
};

/* ---- what the form allows ------------------------------------------------- */

export const isSnapshotForm = (form: MetricForm): boolean => isSnapshotMetric(form.subCategory);

export const isFeedForm = (form: MetricForm): boolean => isFeedCategory(form.category);

/**
 * A dispatch metric and a snapshot are KPI-only, as they are on the dashboard:
 * the job board answers a count for right now and a snapshot is an all-time
 * figure, and neither has a series a chart could draw.
 */
export const availableTypes = (form: MetricForm): MetricType[] =>
  isFeedForm(form) || isSnapshotForm(form) ? ['kpi'] : ['barChart', 'lineChart', 'kpi'];

/**
 * What the dialog actually offers, which is `availableTypes` plus whatever the
 * form is already set to.
 *
 * A row can already be something this category would not offer today — a
 * dispatch metric saved as a chart, which this console draws fine and the
 * rules above would not let anyone create. Offering only the allowed set would
 * leave no button selected, and saving would silently convert a card on the
 * dispatch dashboard too. So the current value stays on the list until the
 * operator picks something else, at which point it drops off by itself.
 */
export const offeredTypes = (form: MetricForm): MetricType[] => {
  const allowed = availableTypes(form);
  return allowed.includes(form.type) ? allowed : [...allowed, form.type];
};

/**
 * Why a subcategory cannot be picked, or null. A chart cannot draw a snapshot
 * (there is no window on it), and it cannot mix money with counts because the
 * two share one axis.
 */
export const disabledReason = (code: string, form: MetricForm): string | null => {
  if (form.type === 'kpi') return null;
  if (isSnapshotSubcategory(code)) {
    return `${subcategoryLabel(code)} is KPI-only — it is an all-time figure with no period to chart.`;
  }
  const chosen = form.subCategory.filter((picked) => picked !== code);
  if (!chosen.length) return null;
  const kind = sharedValueKind(chosen);
  if (kind && valueKindOf(code) !== kind) {
    return `${subcategoryLabel(code)} is ${valueKindOf(code) === 'currency' ? 'an amount' : 'a count'}, and this chart draws ${kind === 'currency' ? 'amounts' : 'counts'}.`;
  }
  return null;
};

/**
 * The picks that survive a change of type — `_sanitizeSubCategoriesForType`.
 * A KPI draws exactly one; a chart drops anything of the other value kind
 * rather than silently drawing dollars against a count axis.
 */
export const sanitizeSubcategories = (codes: string[], type: MetricType): string[] => {
  if (!codes.length) return codes;
  if (type === 'kpi') return [codes[0]];
  const kind = valueKindOf(codes[0]);
  return codes.filter((code) => valueKindOf(code) === kind);
};

/** Changing the category throws the picks away — they belong to the old one. */
export const withCategory = (form: MetricForm, category: string): MetricForm => {
  const next: MetricForm = { ...form, category: category.toLowerCase(), subCategory: [] };
  const types = availableTypes(next);
  return types.includes(next.type) ? next : { ...next, type: types[0] };
};

export const withType = (form: MetricForm, type: MetricType): MetricForm => ({
  ...form,
  type,
  subCategory: sanitizeSubcategories(form.subCategory, type),
  // The header is a KPI's field; leaving it set on a chart would write it into
  // `arrangeBy`, where an arrangement belongs.
  header: type === 'kpi' ? form.header : '',
});

/** Picking a subcategory, then re-settling the type it may now be. */
export const withSubcategory = (form: MetricForm, code: string, on: boolean): MetricForm => {
  const picked = on
    ? [...form.subCategory.filter((entry) => entry !== code), code]
    : form.subCategory.filter((entry) => entry !== code);
  const next: MetricForm = { ...form, subCategory: sanitizeSubcategories(picked, form.type) };
  const types = availableTypes(next);
  return types.includes(next.type) ? next : withType(next, types[0]);
};

/* ---- the name ------------------------------------------------------------- */

/**
 * The name offered when the operator has typed none.
 *
 * `Category - Sub/Sub`, and deliberately WITHOUT the period part the
 * dashboard's `generateMetricsName` appends: that part names a time shape this
 * screen does not let anyone choose, so printing it here would be naming a
 * decision the operator did not make. It is only ever a suggestion — the field
 * is free text and the saved value is what both screens show.
 */
export const suggestedName = (form: MetricForm): string => {
  // The LABEL, not the id capitalized: `drivers` is called Chauffeurs
  // everywhere a person reads it, on both screens.
  const category = form.category ? categoryLabel(form.category) : '';
  const subs = form.subCategory.map(subcategoryLabel).join('/');
  if (!subs) return category;
  return `${category} - ${subs}`;
};

/* ---- validating and writing ----------------------------------------------- */

/** The refusal to show, or null. Mirrors the dashboard dialog's `_submit`. */
export const validateForm = (form: MetricForm): string | null => {
  if (!form.category) return 'Pick a category.';
  if (!form.subCategory.length) return 'Pick at least one metric.';
  if (form.type === 'kpi' && form.subCategory.length > 1) return 'A KPI draws exactly one metric.';
  if (form.type !== 'kpi' && sharedValueKind(form.subCategory) === null) {
    return 'A chart must draw all counts or all amounts, not both.';
  }
  return null;
};

export interface MetricWrite {
  category: string;
  subCategory: string[];
  metricsName: string;
  groupBy: string;
  arrangeBy: string;
  type: string;
  priority: number;
}

/**
 * The whole form dispatch's create and update both take.
 *
 * `current` is the row being edited, and its only job is to let the stored time
 * shape survive an edit made from a screen that cannot see it. On a create
 * there is none and the defaults apply.
 *
 * `priority: 0` on a create puts the new card last, which is what the dashboard
 * does — and `reorder` re-strikes every priority from the top anyway, so the
 * first drag settles it.
 */
export const writePayload = (form: MetricForm, current?: DispatchMetric | null): MetricWrite => {
  const snapshot = isSnapshotForm(form);
  const stored = normalizeBucket(current?.groupBy ?? '');

  let groupBy: string;
  if (snapshot) {
    groupBy = SNAPSHOT_BUCKET;
  } else if (!stored) {
    // No stored grouping, or it was `all` and this metric is no longer a
    // snapshot: it needs a real one back.
    groupBy = DEFAULT_BUCKET;
  } else if (isFeedForm(form) && !FEED_BUCKETS.includes(stored)) {
    groupBy = DEFAULT_BUCKET;
  } else {
    groupBy = stored;
  }

  const arrangeBy =
    form.type === 'kpi'
      ? form.header.trim()
      : coerceArrangeBy(groupBy === SNAPSHOT_BUCKET ? DEFAULT_BUCKET : groupBy, current?.arrangeBy ?? '');

  return {
    category: form.category,
    subCategory: form.subCategory,
    metricsName: form.metricsName.trim() || suggestedName(form),
    groupBy,
    arrangeBy,
    type: form.type,
    priority: Number(current?.priority) || 0,
  };
};

/** The header choices the dialog offers, plus whatever a tenant already typed. */
export const headerPresets = (current: string): string[] => {
  const presets = Object.values(KPI_PRESETS) as string[];
  const typed = normalizeKpiHeader(current);
  return presets.some((preset) => preset.toLowerCase() === typed.toLowerCase())
    ? presets
    : [...presets, typed];
};

export const subcategoryOptions = (category: string) => subcategoriesFor(category);
